import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, AlertTriangle, Mail } from "lucide-react";

const STATUS_LABELS = {
  draft: "Draft",
  sent: "Sent",
  accepted: "Accepted",
  signed: "Signed",
  deposit_paid: "Deposit Paid",
  partial_paid: "Partially Paid",
  paid: "Paid",
  voided: "Voided",
  inactive: "Inactive",
};

function formatSignedAt(date) {
  if (!date) return "";
  try {
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

const LOCKED_STATES = new Set(["accepted", "signed", "deposit_paid", "partial_paid", "paid"]);

const QuoteHeader = ({
  isEditing,
  saving,
  onBack,
  onSave,
  onSend,
  canSend,
  status,
  signature,
}) => {
  const showLockedWarning = !!signature || LOCKED_STATES.has(status);
  const statusLabel = STATUS_LABELS[status] || status;

  return (
    <header className="bg-white border-b border-zinc-200 sticky top-0 z-20">
      <div className="px-8 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto gap-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onBack} className="text-zinc-600 hover:text-[#00341d]" data-testid="quote-back-btn">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#00341d]">
                {isEditing ? "Edit Quote" : "New Quote"}
              </h1>
              {isEditing && status && (
                <span
                  className="text-xs uppercase tracking-wider px-2 py-1 rounded-full bg-zinc-100 text-zinc-700 font-medium"
                  data-testid="quote-status-badge"
                >
                  {statusLabel}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditing && onSend && (
              <Button
                variant="outline"
                onClick={onSend}
                disabled={!canSend}
                title={canSend ? "Send this quote to the customer" : "Save quote and add customer email first"}
                className="border-[#00341d]/30 text-[#00341d] hover:bg-[#00341d]/5 h-11"
                data-testid="builder-send-quote-btn"
              >
                <Mail className="w-5 h-5 mr-2" />
                Send to Customer
              </Button>
            )}
            <Button
              onClick={onSave}
              disabled={saving}
              className="bg-[#00341d] hover:bg-[#002816] text-white font-bold uppercase tracking-wide px-6 h-11 rounded-sm"
              data-testid="save-quote-button"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </span>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Quote
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {isEditing && showLockedWarning && (
        <div
          className="bg-amber-50 border-t border-amber-200"
          data-testid="quote-locked-warning"
        >
          <div className="max-w-7xl mx-auto px-8 py-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 leading-relaxed">
              <p className="font-semibold">
                You're editing a {statusLabel?.toLowerCase() || "post-acceptance"} quote.
              </p>
              <p className="text-amber-800 mt-0.5">
                {signature ? (
                  <>
                    The customer ({signature.name}) signed this quote
                    {signature.date ? ` on ${formatSignedAt(signature.date)}` : ""}.
                    Changes won't update the existing signature — if pricing or scope changes
                    materially, consider creating a <span className="font-medium">Change Order</span> instead so the customer re-acknowledges.
                  </>
                ) : (
                  <>Changes after acceptance bypass the customer's original sign-off. Prefer a <span className="font-medium">Change Order</span> for material edits.</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default QuoteHeader;
